import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Disclaimer from "../../components/Disclaimer/Disclaimer";
import Layout from "../../components/Layout/Layout";
import { REGIONS, SITES } from "../../data/sites";
import { bortleLabel, bortleTone, matchesFilters, TAG_LABELS } from "../../lib/access";
import type { Site } from "../../types";

const Sites = () => {
  const [region, setRegion] = useState<Site["region"] | "">("");
  const [driveUp, setDriveUp] = useState(false);
  const [overnight, setOvernight] = useState(false);
  const [lengthFriendly, setLengthFriendly] = useState(false);

  const filtered = useMemo(
    () => SITES.filter((site) => matchesFilters(site, { region, driveUp, overnight, lengthFriendly })),
    [region, driveUp, overnight, lengthFriendly],
  );

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 py-12 w-full">
        <p className="text-sun font-semibold uppercase tracking-wide text-sm">
          Curated starting points
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-alt-blk mt-3">
          Van-accessible dark skies
        </h1>
        <p className="text-xl text-alt-blk/80 mt-4 max-w-3xl leading-relaxed">
          A modest list, still weighted toward Texas, the Southwest, and the
          West, with starting points in Alaska, Canada, and Mexico. These are
          places you can actually sleep — not just drive to for an evening.
          Rules change; read the notes and check the land manager.
        </p>
        <div className="mt-4">
          <Disclaimer />
        </div>

        <div className="flex flex-wrap gap-2 mt-8">
          {(["", ...REGIONS] as const).map((value) => (
            <button
              key={value || "all"}
              className={`text-sm px-4 py-1.5 rounded-full border ${
                region === value
                  ? "bg-mars border-mars text-white"
                  : "border-alt-blk/20 hover:border-mars"
              }`}
              onClick={() => setRegion(value)}
            >
              {value || "All regions"}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Chip label="Drive-up" active={driveUp} onClick={() => setDriveUp((v) => !v)} />
          <Chip label="Overnight" active={overnight} onClick={() => setOvernight((v) => !v)} />
          <Chip
            label="Larger-van friendly"
            active={lengthFriendly}
            onClick={() => setLengthFriendly((v) => !v)}
          />
        </div>

        <p className="text-sm text-alt-blk/60 mt-6">
          {filtered.length} site{filtered.length === 1 ? "" : "s"}
        </p>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {filtered.map((site) => (
            <article
              key={site.id}
              className="rounded-2xl bg-white border border-alt-blk/10 p-6 flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-sun">
                    {site.region} · {site.state}
                  </p>
                  <h2 className="text-2xl font-bold mt-1">{site.name}</h2>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-turquoise">
                    {bortleLabel(site.bortle)}
                  </div>
                  <div className="text-xs text-alt-blk/50">{bortleTone(site.bortle)}</div>
                </div>
              </div>
              <p className="mt-3 text-alt-blk/80 leading-relaxed">{site.whyGood}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {site.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-alt-blk/15 text-alt-blk/70"
                  >
                    {TAG_LABELS[tag]}
                  </span>
                ))}
              </div>
              <p className="text-sm text-alt-blk/70 mt-4">{site.accessNotes}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/sites/${site.id}`}
                  className="inline-block bg-mars hover:bg-sunset text-white px-5 py-2.5 rounded-2xl font-semibold text-sm"
                >
                  Practical notes
                </Link>
                <Link
                  to={`/?site=${site.id}`}
                  className="inline-block border border-alt-blk px-5 py-2.5 rounded-2xl font-semibold text-sm hover:bg-alt-blk hover:text-white transition-colors"
                >
                  Show on map
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
};

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`text-sm px-4 py-1.5 rounded-full border ${
        active ? "bg-mars border-mars text-white" : "border-alt-blk/20 hover:border-mars"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default Sites;
